import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { isEqual } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { ACTUAL_CLINIC_TYPES } from "constants/clinics";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useScheduleExperience } from "features/scheduling/hooks/use-schedule-experience.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import {
  CLINICS_MINIMAL_QUERY,
  useLazyGetClinicsQuery
} from "services/clinics";
import { Clinic, ClinicsResponse } from "services/types/clinics.type";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import {
  applyClinicsFilter,
  clinicsFilterReady,
  selectClinicsFilterIsReady
} from "../../scheduler-slice";
import {
  computeEffectiveClinicSelection,
  computeSelectedClinics
} from "./use-clinics-filter.utils";
import useStorage from "./use-storage.hook";

type ClinicsFilterProviderProps = {
  children?: React.ReactNode;
  disableStorage?: boolean;
};

type Ctx = {
  selected: Clinic[];
  clinics: ClinicsResponse | undefined;
  selectAll: () => void;
  deselectAll: () => void;
  handleChange: (value: Clinic[]) => void;
  showServiceLocations: boolean;
  setShowServiceLocations: (value: boolean) => void;
  persistCurrentClinicSelected: (value: boolean) => void;
  showCurrentClinicEventsOnly: boolean;
  setShowCurrentClinicEventsOnly: (value: boolean) => void;
};

const ClinicsFilterContext = createContext<Ctx | null>(null);

export function ClinicsFilterProvider({
  children,
  disableStorage
}: ClinicsFilterProviderProps) {
  const {
    defaultClinics: defaultValues,
    updateStorage,
    contextKey
  } = useStorage(disableStorage);
  const currentClinicSelectedStorage = useMemo(
    () => setupLocalStorageAPI("currentClinicSelected"),
    []
  );
  const showServiceLocationsStorage = useMemo(
    () => setupLocalStorageAPI("showServiceLocations"),
    []
  );

  const {
    isScheduleV2Enabled: schedulingV2FeatureEnabled,
    isSchedule21Enabled
  } = useScheduleExperience();

  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegion = useAppSelector(selectCurrentRegion);
  const currentUser = useAppSelector(selectCurrentUser);
  const isReady = useAppSelector(selectClinicsFilterIsReady);

  const { userPermissions } = useUserRoles();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];

  const [selected, setSelected] = useState<Clinic[]>([]);
  // ENG-2288: saved like "Show <clinic> events only" below, because this provider unmounts
  // whenever the user leaves /scheduling (a patient's chart) and came back unticked.
  const [showServiceLocations, setShowServiceLocationsState] = useState(
    () =>
      !disableStorage &&
      showServiceLocationsStorage.getData(
        false,
        (data: unknown) => typeof data === "boolean"
      )
  );
  const setShowServiceLocations = useCallback(
    (value: boolean) => {
      setShowServiceLocationsState(value);
      if (!disableStorage) showServiceLocationsStorage.updateData(value);
    },
    [disableStorage, showServiceLocationsStorage]
  );
  const [showCurrentClinicEventsOnly, setShowCurrentClinicEventsOnly] =
    useState(() =>
      currentClinicSelectedStorage.getData(isSchedule21Enabled ? true : false)
    );

  // Sync showCurrentClinicEventsOnly when the 2.1 flag first activates (LD async load race)
  const prevIsSchedule21Ref = useRef(isSchedule21Enabled);
  useEffect(() => {
    if (isSchedule21Enabled && !prevIsSchedule21Ref.current) {
      setShowCurrentClinicEventsOnly(
        currentClinicSelectedStorage.getData(true)
      );
    }
    prevIsSchedule21Ref.current = isSchedule21Enabled;
  }, [isSchedule21Enabled, currentClinicSelectedStorage]);

  // Track if user has manually changed selection
  const userTouchedRef = useRef(false);
  const prevContextKey = useRef(contextKey);

  // ----- Stable params & key -----
  const params = useMemo(
    () => ({
      // Consumers read only id/name/timezone (and fullName from practice.name); the full CLINICS_QUERY
      // made this the heaviest /api/clinics/ caller in production (ENG-2088).
      query: CLINICS_MINIMAL_QUERY,
      type: showServiceLocations ? undefined : ACTUAL_CLINIC_TYPES,
      region: currentRegion?.id,
      user:
        currentRegion ||
        viewOwnScheduleOnly ||
        currentUser?.show_own_clinics_only
          ? currentUser?.id
          : undefined
    }),
    [
      currentRegion,
      currentUser?.id,
      showServiceLocations,
      viewOwnScheduleOnly,
      currentUser?.show_own_clinics_only
    ]
  );

  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  const readyToFetch = useMemo(
    () => Boolean(currentClinic && currentUser?.id),
    [currentClinic, currentUser?.id]
  );

  const [getClinics, { data: clinics = [], isFetching, isUninitialized }] =
    useLazyGetClinicsQuery();
  const isLoading = isUninitialized || isFetching;

  // Fire exactly once per effective params key
  const lastFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!readyToFetch) return;
    if (lastFetchKeyRef.current === paramsKey) return;
    lastFetchKeyRef.current = paramsKey;
    getClinics(params);
  }, [readyToFetch, paramsKey, getClinics, params]);

  useEffect(() => {
    if (contextKey !== prevContextKey.current) {
      // Clinic filter: Clear user-touched flag and selections
      userTouchedRef.current = false;
      setSelected([]);
      initializedRef.current = false;
      lastFetchKeyRef.current = null;
      prevContextKey.current = contextKey;
    }
  }, [contextKey]);

  // ----- Selection helpers -----
  const handleChange = useCallback(
    (value: Clinic[]) => {
      const newValues = value.filter(Boolean);
      userTouchedRef.current = true; // Mark as user-touched
      setSelected(newValues);
      updateStorage(
        "clinics",
        newValues.map(({ id }) => id)
      );
    },
    [updateStorage]
  );

  const selectAll = useCallback(
    () => handleChange(clinics),
    [handleChange, clinics]
  );
  const deselectAll = useCallback(() => handleChange([]), [handleChange]);

  const persistCurrentClinicSelected = useCallback(
    (value: boolean) => {
      if (!disableStorage) {
        currentClinicSelectedStorage.updateData(value);
      }
    },
    [disableStorage, currentClinicSelectedStorage]
  );

  const selectedClinics = useMemo(
    () =>
      computeSelectedClinics({
        isSchedule21Enabled,
        schedulingV2FeatureEnabled,
        showCurrentClinicEventsOnly,
        currentClinic,
        clinics,
        defaultValues: defaultValues ?? null
      }),
    [
      defaultValues,
      clinics,
      schedulingV2FeatureEnabled,
      isSchedule21Enabled,
      showCurrentClinicEventsOnly,
      currentClinic
    ]
  );

  // Initialize selection when data loads / changes materially
  const initializedRef = useRef(false);
  useEffect(() => {
    if (isLoading) return;

    const shouldInit =
      !initializedRef.current ||
      (!userTouchedRef.current && !isEqual(selectedClinics, selected)) ||
      !isReady;

    if (shouldInit) {
      // Only reset if user hasn't manually changed selection
      if (!userTouchedRef.current) {
        setSelected(selectedClinics);
        updateStorage(
          "clinics",
          selectedClinics.map(({ id }) => id)
        );
      }
      dispatch(clinicsFilterReady());
      initializedRef.current = true;
    }
  }, [isLoading, selectedClinics, selected, isReady, updateStorage, dispatch]);

  // Apply clinics filter to store only when the effective list changes
  const lastAppliedIdsRef = useRef<number[] | null>(null);
  useEffect(() => {
    if (!isReady) return;

    const currentClinicSelected = computeEffectiveClinicSelection({
      disableStorage,
      isSchedule21Enabled,
      showCurrentClinicEventsOnly,
      schedulingV2FeatureEnabled,
      currentClinicSelectedFromStorage:
        currentClinicSelectedStorage.getData(false),
      currentClinic,
      selected
    });

    const ids =
      currentClinicSelected?.filter(Boolean)?.map(({ id }) => id) ?? [];

    if (
      !lastAppliedIdsRef.current ||
      !isEqual(lastAppliedIdsRef.current, ids)
    ) {
      lastAppliedIdsRef.current = ids;
      dispatch(applyClinicsFilter(ids));
    }
  }, [
    isReady,
    isSchedule21Enabled,
    showCurrentClinicEventsOnly,
    schedulingV2FeatureEnabled,
    currentClinicSelectedStorage,
    currentClinic,
    clinics,
    selected,
    disableStorage,
    dispatch
  ]);

  const value = useMemo<Ctx>(() => {
    return {
      selected,
      clinics,
      handleChange,
      selectAll,
      deselectAll,
      showServiceLocations,
      setShowServiceLocations,
      persistCurrentClinicSelected,
      showCurrentClinicEventsOnly,
      setShowCurrentClinicEventsOnly
    };
  }, [
    selected,
    clinics,
    handleChange,
    selectAll,
    deselectAll,
    showServiceLocations,
    setShowServiceLocations,
    persistCurrentClinicSelected,
    showCurrentClinicEventsOnly,
    setShowCurrentClinicEventsOnly
  ]);

  return (
    <ClinicsFilterContext.Provider value={value}>
      {children}
    </ClinicsFilterContext.Provider>
  );
}

export default function useClinics() {
  const ctx = useContext(ClinicsFilterContext);
  if (!ctx)
    throw new Error("useClinics must be used within a ClinicsFilterProvider");
  return ctx;
}
